import { useContext, useSyncExternalStore } from 'react'

import type { UseAuthInfoProps } from '@sweetauth/core/types'

import { AuthStoreContext } from './store'

/**
 * Returns the three-state discriminated union from the Keycloak BFF auth store.
 *
 * `accessToken` holds the short-lived Keycloak access token (JWT) kept in memory;
 * the ~230 consumer call sites that read `{ user, accessToken }` work unchanged.
 * Must be called inside an `AuthProvider` tree.
 */
export function useAuthInfo(): UseAuthInfoProps {
  const store = useContext(AuthStoreContext)
  if (store === null) {
    throw new Error(
      'useAuthInfo must be called inside an AuthProvider (@sweetauth/keycloak).',
    )
  }
  // `store.getState().authInfo` is a stable reference until the store replaces it,
  // so useSyncExternalStore re-renders only when authInfo actually changes — same
  // semantics as a zustand selector on `state.authInfo`.
  const getAuthInfo = (): UseAuthInfoProps => store.getState().authInfo
  // Hydrating boundaries must see the original loading snapshot even if a session
  // refresh has already resolved elsewhere in the client tree.
  const getInitialAuthInfo = (): UseAuthInfoProps =>
    store.getInitialState().authInfo
  return useSyncExternalStore(store.subscribe, getAuthInfo, getInitialAuthInfo)
}
